import { notifySuccess, notifyWarning } from "../../services/notificationBus";
import { reportError } from "../../services/errorBus";
// src/user/pages/AssignmentPlayer.jsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";

import { useAuth } from "../../contexts/AuthContext";

import { getQuestionnaire } from "../../services/questionnaires.service";
import { getQuestion } from "../../services/questions.service";
import {
  completeTask,
} from "../../services/camunda.service";
import { getUserAnswers, getMyAssignments } from "../../services/instances.service";
import { Dialog } from "primereact/dialog";
import ModalShell from "../../components/Modal/ModalShell";
import useAssignmentDraft from "./useAssignmentDraft";
import useAssignmentDeadline from "./useAssignmentDeadline";
import { deadlineDate, deadlineHour, assignmentClosed, countdown } from "../../services/assignmentDeadline";
import AssignmentTimerGate from "./AssignmentTimerGate";
import AssignmentDeadlineDialog from "./AssignmentDeadlineDialog";
import { hasAnswer } from "./assignmentDraft";
import QuestionNavigator from "./QuestionNavigator";
import { questionOptions, optionValue, optionLabel } from "../../services/questionOptions";
import "./AssignmentPlayer.css";

/* ---------------- Utils ---------------- */

function mapAnswerType(answerType) {
  switch (answerType) {
    case "SINGLE_OPTION":
      return "single";
    case "MULTIPLE_OPTION":
      return "multi";
    case "OPEN_TEXT":
      return "text";
    case "NUMERIC":
      return "number";
    default:
      return "text";
  }
}

// Para el payload final de userResponses (números que tu backend espera)
function mapQuestionTypeToResponseCode(type) {
  switch (type) {
    case "text":
      return 0;
    case "single":
      return 1;
    case "multi":
      return 2;
    case "number":
      return 3;
    default:
      return 0;
  }
}

// Mapear respuestas del backend (UserAnswerDto) al formato de UI (labels completos)
function mapBackendAnswersToUi(questions, backendAnswers) {
  const result = {};
  const byId = new Map(
    (backendAnswers || []).map((a) => [String(a.questionId), a])
  );

  for (const q of questions) {
    const entry = byId.get(String(q.id));
    if (!entry || !Array.isArray(entry.response) || !entry.response.length) {
      continue;
    }

    const resp = entry.response;

    if (q.type === "multi") {
      result[q.id] = resp.map(code => optionLabel(q.choices, code));
    } else if (q.type === "single") {
      result[q.id] = optionLabel(q.choices, resp[0]);
    } else if (q.type === "number") {
      const num = Number(resp[0]);
      result[q.id] = Number.isNaN(num) ? resp[0] : num;
    } else {
      // text
      result[q.id] = resp[0];
    }
  }

  return result;
}

function StatusChip({ status }) {
  const map = {
    "not-started": { label: "Sin iniciar", severity: "secondary" },
    "in-progress": { label: "En progreso", severity: "warning" },
    submitted: { label: "Enviado", severity: "success" },
  };
  const s = map[status] || map["not-started"];
  return <Tag value={s.label} severity={s.severity} />;
}

// Cuenta cuántas preguntas tienen respuesta "no vacía"
function computeAnswered(questions, answers) {
  return questions.filter(q => hasAnswer(q, answers[q.id])).length;
}

const answerTypeLabels = { text: "Respuesta abierta", number: "Respuesta numérica", single: "Selección única", multi: "Selección múltiple" };

/* ---------------- Component ---------------- */

export default function AssignmentPlayer() {
  const { assignmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [resolved, setResolved] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (location.state?.questionnaireId) return;
    let cancelled = false;
    getMyAssignments().then(rows => {
      if (!cancelled) {
        const row = rows.find(item => item.id === assignmentId);
        if (row) setResolved({ id: assignmentId, state: row });
        else setFailed(true);
      }
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [assignmentId, location.state]);
  const state = location.state?.questionnaireId ? location.state : resolved?.id === assignmentId ? resolved.state : null;
  if (!state) return <div className="p-6" role={failed ? "alert" : "status"}>
    {failed ? "No se pudo encontrar esta asignación. Vuelve a tus asignaciones e inténtalo de nuevo." : "Cargando asignación..."}
  </div>;
  if (state.timeLimitMinutes && !state.readOnly && !state.isHistoric && state.status !== "submitted") {
    return <AssignmentTimerGate key={assignmentId} taskId={assignmentId} initialState={state} onBack={() => navigate("/assignments")}>
      {timedState => <AssignmentPlayerContent initialState={timedState} />}
    </AssignmentTimerGate>;
  }
  return <AssignmentPlayerContent key={assignmentId} initialState={state} />;
}

function AssignmentPlayerContent({ initialState }) {
  const { assignmentId } = useParams(); // taskId de Camunda (runtime u histórico)
  const navigate = useNavigate();
  const { me } = useAuth();

  // Datos que vienen desde MyAssignments
  const {
    questionnaireId,
    questionnaireName: initialQName,
    forUser: initialForUser,
    forUserId,
    due: initialDue,
    status: initialStatus,
    processInstanceId,
    isHistoric = false,
    readOnly: stateReadOnly,
  } = initialState;

  const readOnly = stateReadOnly || initialStatus === "submitted" || isHistoric;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [assignmentMeta, setAssignmentMeta] = useState({
    id: assignmentId, // taskId
    questionnaireId: questionnaireId || null,
    questionnaireName: initialQName || "",
    forUser: initialForUser || "",
    due: initialDue || null,
    status: initialStatus || "not-started",
    processInstanceId: processInstanceId || null,
    isHistoric,
  });

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [confirmIncomplete, setConfirmIncomplete] = useState(false);
  const [submitted, setSubmitted] = useState(readOnly);
  const [leaveTo, setLeaveTo] = useState(null);
  const [validationError, setValidationError] = useState("");
  const questionRoot = useRef(null);
  const questionHeading = useRef(null);
  const contentScroll = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [review, setReview] = useState(false);
  const [navigatorOpen, setNavigatorOpen] = useState(false);
  const [finalAnswersLoading, setFinalAnswersLoading] = useState(!!readOnly);
  const [finalAnswersError, setFinalAnswersError] = useState(false);
  const [historyRetry, setHistoryRetry] = useState(0);

  // Índice actual
  const [idx, setIdx] = useState(0);

  const total = questions.length;
  const answeredCount = computeAnswered(questions, answers);
  const missingRequired = questions.filter(q => q.required && !hasAnswer(q, answers[q.id])).length;

  const canPrev = idx > 0;
  const canNext = idx < total - 1;

  const current = questions[idx];
  const draft = useAssignmentDraft({ taskId: assignmentId, questionnaireId: assignmentMeta.questionnaireId,
    userId: me?.id, questions, answers, setAnswers, readOnly: !!readOnly });
  const deadline = useAssignmentDeadline({ taskId: assignmentId, questionnaireId: assignmentMeta.questionnaireId,
    dueAt: initialState.dueAt, hardDeadline: initialState.hardDeadline, clockOffset: initialState.clockOffset,
    deadlineAt: initialState.deadlineAt, timerExpiresAt: initialState.timerExpiresAt,
    readOnly: !!readOnly || submitted, answersRef, draft, submitting });
  const savingDraft = draft.status === "saving";
  const locked = submitting || (readOnly ? finalAnswersLoading || finalAnswersError : !draft.ready || !!draft.recovery);
  const goToQuestion = useCallback(index => {
    setIdx(index);
    setReview(false);
    setNavigatorOpen(false);
    setValidationError("");
    requestAnimationFrame(() => {
      if (contentScroll.current) contentScroll.current.scrollTop = 0;
      questionHeading.current?.focus();
    });
  }, []);
  const showReview = () => {
    setReview(true);
    setNavigatorOpen(false);
    setValidationError("");
    requestAnimationFrame(() => {
      if (contentScroll.current) contentScroll.current.scrollTop = 0;
      questionHeading.current?.focus();
    });
  };
  const requestLeave = useCallback(path => {
    if (submitting) return;
    if (!readOnly && !deadline.closed && (draft.dirty || savingDraft)) setLeaveTo(path);
    else navigate(path);
  }, [submitting, readOnly, deadline.closed, draft.dirty, savingDraft, navigate]);
  useEffect(() => {
    if (readOnly || (!draft.dirty && !savingDraft && !submitting)) return;
    const guardLink = event => {
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || anchor.target === "_blank") return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      event.preventDefault(); event.stopPropagation();
      requestLeave(url.pathname + url.search + url.hash);
    };
    document.addEventListener("click", guardLink, true);
    return () => document.removeEventListener("click", guardLink, true);
  }, [readOnly, draft.dirty, savingDraft, submitting, requestLeave]);

  /* ---------------- Cargar cuestionario + preguntas ---------------- */

  useEffect(() => {
    const qId = assignmentMeta.questionnaireId;

    if (!qId) {
      setLoadError("Falta información del cuestionario para esta asignación.");
      setLoading(false);
      return;
    }

    const run = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        // 1) Obtener cuestionario (lista de preguntas)
        const q = await getQuestionnaire(qId);

        // 2) Tomar las preguntas base y ORDENARLAS por position
        const baseQuestionsRaw = Array.isArray(q.questions) ? q.questions : [];
        const baseQuestions = [...baseQuestionsRaw].sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0)
        );

        // 3) Para cada pregunta, obtener detalle con answers y answerType
        const fullQuestions = await Promise.all(
          baseQuestions.map(async (qBase) => {
            const qFull = await getQuestion(qBase.id);
            const type = mapAnswerType(qFull.answerType);
            const choices = questionOptions(qFull.answers);

            return {
              id: qFull.id,
              text: qFull.text,
              required: qBase.required,
              type,
              options: choices.map(option => option.label),
              choices,
              position: qBase.position,
            };
          })
        );

        setAssignmentMeta((prev) => ({
          ...prev,
          questionnaireName: q.name || prev.questionnaireName,
        }));

        setQuestions(fullQuestions);
      } catch (err) {
        console.error(
          "[AssignmentPlayer] Error loading questionnaire/questions",
          err
        );
        setLoadError("No se pudo cargar esta asignación. Intenta de nuevo más tarde.");
      } finally {
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentMeta.questionnaireId]);

  /* ---------------- Hidratar respuestas finales desde backend (modo read-only / histórico) ---------------- */

  useEffect(() => {
    if (!readOnly || !questions.length) return;
    let cancelled = false;

    const loadFinalAnswers = async () => {
      try {
        setFinalAnswersLoading(true);
        setFinalAnswersError(false);
        // Si es manager-fills-for-employee, usamos forUserId.
        // Si es self-assignment, usamos me.id.
        const userIdToUse = forUserId || me?.id;
        if (!userIdToUse || !assignmentMeta.processInstanceId) throw new Error("Missing historical answer context");

        const dto = await getUserAnswers(
          assignmentMeta.processInstanceId,
          userIdToUse
        );
        const mapped = mapBackendAnswersToUi(questions, dto.answers || []);
        if (cancelled) return;
        setAnswers(mapped);
        setSubmitted(true);
        setIdx(0);
        setAssignmentMeta((prev) => ({
          ...prev,
          status: "submitted",
        }));
      } catch (err) {
        if (cancelled) return;
        setFinalAnswersError(true);
        console.error("[AssignmentPlayer] Error loading final answers", err);
      } finally {
        if (!cancelled) setFinalAnswersLoading(false);
      }
    };

    loadFinalAnswers();
    return () => { cancelled = true; };
  }, [readOnly, assignmentMeta.processInstanceId, forUserId, me?.id, questions, historyRetry]);

  /* ---------------- Responder ---------------- */

  const setAnswer = useCallback((qid, val) => {
    if (assignmentClosed(initialState)) return;
    setValidationError("");
    answersRef.current = { ...answersRef.current, [qid]: val };
    setAnswers(answersRef.current);
  }, [initialState]);

  const onNext = () => {
    if (canNext) goToQuestion(idx + 1);
  };

  const onPrev = () => {
    if (canPrev) goToQuestion(idx - 1);
  };

  /* ---------------- Guardar progreso (draftResponses + progressMeter) ---------------- */

  const handleSaveProgress = async () => {
    if (await draft.save()) notifySuccess("Progreso guardado.");
  };

  /* ---------------- Submit final (completeTask + userResponses) ---------------- */

  const onSubmit = useCallback(
    async (allowIncomplete = false) => {
      if (readOnly || submitting || deadline.closed || !draft.ready || draft.recovery) return;
      if (questions.some(q => !hasAnswer(q, answers[q.id])) && allowIncomplete !== true) {
        setConfirmIncomplete(true);
        return;
      }
      setConfirmIncomplete(false);

      if (!assignmentMeta.id) {
        notifyWarning("No hay una tarea activa disponible. Vuelve a tus asignaciones y abre nuevamente el cuestionario.");
        return;
      }

      if (!questions.length) {
        notifyWarning("No hay preguntas para enviar.");
        return;
      }

      try {
        setSubmitting(true);
        await draft.pause();

        // Construir el array de respuestas en el formato para el delegate
        const responsesPayload = [];

        for (const q of questions) {
          const raw = answers[q.id];

          let responseArray = [];

          if (q.type === "multi") {
            if (Array.isArray(raw)) {
              responseArray = raw
                .map((opt) => optionValue(q.choices, opt))
                .filter((s) => s);
            }
          } else if (
            raw !== undefined &&
            raw !== null &&
            String(raw).trim() !== ""
          ) {
            if (q.type === "single") {
              responseArray = [optionValue(q.choices, raw)];
            } else {
              // text / number
              responseArray = [String(raw)];
            }
          }

          if (!responseArray.length) {
            continue;
          }

          responsesPayload.push({
            questionId: q.id,
            type: mapQuestionTypeToResponseCode(q.type),
            response: responseArray,
          });
        }

        await completeTask(assignmentMeta.id, {
          userResponses: {
            value: JSON.stringify(responsesPayload),
            type: "String",
          },
        });

        draft.complete();
        setSubmitted(true);
        setAssignmentMeta((prev) => ({
          ...prev,
          status: "submitted",
        }));

        notifySuccess("Tus respuestas fueron enviadas. Gracias.");
        navigate("/assignments");
      } catch (err) {
        draft.resume();
        console.error("[AssignmentPlayer] Error submitting assignment", err);
        reportError(err, "Hubo un error al enviar tus respuestas. Inténtalo de nuevo sin cerrar esta pantalla.");
      } finally {
        setSubmitting(false);
      }
    },
    [readOnly, submitting, deadline.closed, draft, assignmentMeta.id, answers, questions, navigate]
  );

  /* ---------------- Render estados especiales ---------------- */

  if (!assignmentMeta.questionnaireId) {
    return (
      <div className="h-full overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-xl border bg-white dark:bg-gray-800 p-6">
              <h2 className="text-xl font-semibold mb-2">Asignación no encontrada</h2>
              <p className="text-sm text-muted-foreground mb-4">
                No se recibió información de cuestionario para esta asignación.
              </p>
              <Button
                label="Volver a asignaciones"
                onClick={() => requestLeave("/assignments")}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading || (!readOnly && questions.length > 0 && !draft.ready && draft.status !== "load-error")) {
    return (
      <div className="h-full overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-xl border bg-white dark:bg-gray-800 p-6 text-sm text-muted-foreground">
              Cargando asignación...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="h-full overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-xl border bg-white dark:bg-gray-800 p-6">
              <h2 className="text-xl font-semibold mb-2">Error</h2>
              <p className="text-sm text-muted-foreground mb-4">{loadError}</p>
              <Button
                label="Volver a asignaciones"
                onClick={() => requestLeave("/assignments")}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const headerStatus = submitted || deadline.status === "saved" ? "submitted" : answeredCount || initialState.startedAt ? "in-progress" : assignmentMeta.status;
  const downloadDeadlineBackup = () => {
    const payload = { questionnaire: assignmentMeta.questionnaireName, dueAt: initialState.dueAt, deadlineAt: initialState.deadlineAt,
      questions: questions.map(question => ({ question: question.text, response: deadline.snapshot?.answers?.[question.id] ?? null })) };
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }));
    const link = document.createElement("a"); link.href = url; link.download = "teamassess-respuestas.json"; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const saveError = draft.status === "load-error" || draft.status === "error";
  const saveText = draft.status === "load-error" ? "No se pudo recuperar el progreso. Reintenta antes de responder."
    : draft.status === "error" ? "No se pudo guardar en el servidor. Tus respuestas siguen en esta pestaña."
    : savingDraft ? "Guardando progreso..." : draft.dirty ? "Cambios pendientes" : "Progreso guardado";

  return (
    <div className="assignment-player teamassess-form">
      <header className="player-header">
        <div className="player-header-top">
          <Button text icon="pi pi-arrow-left" label="Mis asignaciones" disabled={submitting} onClick={() => requestLeave("/assignments")} />
          {!readOnly && <Button outlined icon="pi pi-save" label="Guardar progreso" disabled={savingDraft || locked || deadline.closed} onClick={handleSaveProgress} />}
        </div>
        <div className="player-title-row">
          <div className="player-title">
            <h1 title={assignmentMeta.questionnaireName}>{assignmentMeta.questionnaireName || "Cuestionario"}</h1>
            <div className="player-metadata">
              <span><i className="pi pi-user" aria-hidden="true" /> Para: <strong>{assignmentMeta.forUser || "Ti"}</strong></span>
              {(assignmentMeta.due || initialState.dueAt) && <span className={deadline.expired && !readOnly ? "player-deadline-overdue" : ""}>
                <i className="pi pi-calendar" aria-hidden="true" /> Vence: {deadlineDate({ ...initialState, due: assignmentMeta.due })}
                {initialState.dueAt && ` · ${deadlineHour(initialState)}`}
              </span>}
              {initialState.hardDeadline && <span><i className="pi pi-lock" aria-hidden="true" /> Cierre automático</span>}
              {readOnly && <span><i className="pi pi-lock" aria-hidden="true" /> Solo lectura</span>}
            </div>
          </div>
          <div className="player-status-group">
            {initialState.timerExpiresAt && !readOnly && <div className={`player-timer${deadline.remainingSeconds <= 300 ? " is-urgent" : ""}`}>
              <span>Tiempo restante</span><strong role="timer" aria-live="off" aria-label="Tiempo restante">{countdown(deadline.remainingSeconds)}</strong>
            </div>}
            <StatusChip status={headerStatus} />
          </div>
        </div>
        {deadline.closed && <div className="player-deadline-notice" role="status" aria-live="polite">
          <i className="pi pi-lock" aria-hidden="true" />
          <span>{deadline.status === "saved" ? "Plazo finalizado. Tus respuestas fueron enviadas, incluidas las preguntas sin responder."
            : deadline.status === "failed" ? "Plazo finalizado. No se pudo confirmar el último envío. El cierre del servidor usará el progreso recibido. Tus cambios pendientes siguen disponibles en esta pestaña."
              : "El plazo terminó. La edición está bloqueada mientras se envían tus últimas respuestas."}</span>
          {deadline.status === "failed" && <Button text icon="pi pi-download" label="Descargar respaldo" onClick={downloadDeadlineBackup} />}
        </div>}
        {!readOnly && !deadline.closed && <div className={`assignment-save-status${saveError ? " is-error" : ""}`} role="status" aria-live="polite">
          <i aria-hidden="true" className={`pi ${saveError ? "pi-exclamation-circle" : savingDraft ? "pi-spin pi-spinner" : draft.dirty ? "pi-clock" : "pi-check-circle"}`} />
          <span>{saveText}</span>
          {draft.status === "load-error" && <Button type="button" icon="pi pi-refresh" text label="Reintentar" onClick={draft.retry} />}
          {draft.status === "error" && <Button type="button" icon="pi pi-refresh" text label="Reintentar guardado" onClick={handleSaveProgress} disabled={locked} />}
          {draft.storageFailed && <p role="alert">No se pudo respaldar en esta pestaña. Guarda el progreso antes de salir.</p>}
        </div>}
      </header>

      <div className="player-workspace">
        <button type="button" className="player-mobile-index" aria-controls="player-question-index" aria-expanded={navigatorOpen}
          onClick={() => setNavigatorOpen(open => !open)}>
          <i className="pi pi-list" aria-hidden="true" /><span>{navigatorOpen ? "Ocultar preguntas" : "Mostrar preguntas"}</span>
          <strong>{answeredCount}/{total}</strong><i className={`pi ${navigatorOpen ? "pi-chevron-up" : "pi-chevron-down"}`} aria-hidden="true" />
        </button>
        <aside id="player-question-index" className={`player-index${navigatorOpen ? " is-open" : ""}`}>
          <QuestionNavigator questions={questions} answers={answers} index={idx} review={review} readOnly={readOnly}
            locked={locked} onSelect={goToQuestion} onReview={showReview} />
        </aside>
        <section className="player-main" aria-label={review ? "Revisión de respuestas" : "Pregunta actual"}>
          <div className="player-content-scroll" ref={contentScroll}>
            <div className="player-content">
              {readOnly && finalAnswersError ? (
                <div className="player-empty" role="alert">
                  <i className="pi pi-exclamation-circle" aria-hidden="true" />
                  <h2>No se pudieron cargar tus respuestas</h2>
                  <Button label="Reintentar" icon="pi pi-refresh" outlined onClick={() => setHistoryRetry(value => value + 1)} />
                </div>
              ) : readOnly && finalAnswersLoading && total > 0 ? (
                <div className="player-empty" role="status"><i className="pi pi-spin pi-spinner" aria-hidden="true" /> Cargando respuestas...</div>
              ) : total === 0 ? (
                <div className="player-empty">
                  <i className="pi pi-file" aria-hidden="true" />
                  <h2>No hay preguntas disponibles</h2>
                  <Button label="Volver a asignaciones" icon="pi pi-arrow-left" text onClick={() => requestLeave("/assignments")} />
                </div>
              ) : review ? (
                <>
                  <div className="player-question-meta"><i className="pi pi-list-check" aria-hidden="true" /><span>{readOnly ? "Evaluación enviada" : "Antes de enviar"}</span></div>
                  <h2 ref={questionHeading} tabIndex={-1} className="player-question-title">{readOnly ? "Respuestas enviadas" : "Revisa tus respuestas"}</h2>
                  <div className={`player-review-summary${!readOnly && missingRequired ? " has-pending" : ""}`}>
                    <i className={`pi ${!readOnly && missingRequired ? "pi-exclamation-circle" : "pi-check-circle"}`} aria-hidden="true" />
                    <span>{answeredCount} de {total} respondidas{!readOnly && (missingRequired ? ` · ${missingRequired} ${missingRequired === 1 ? "obligatoria pendiente" : "obligatorias pendientes"}` : " · Sin obligatorias pendientes")}</span>
                  </div>
                  <ol className="player-review-list">
                    {questions.map((question, position) => {
                      const value = answers[question.id];
                      const answered = hasAnswer(question, value);
                      const display = answered ? Array.isArray(value) ? value.join("\n") : String(value) : "Sin respuesta";
                      return <li key={question.id}>
                        <button type="button" disabled={locked} onClick={() => goToQuestion(position)}
                          aria-label={`${readOnly ? "Ver" : "Revisar"} pregunta ${position + 1}: ${question.text}`}>
                          <span className="player-question-number">{position + 1}</span>
                          <span className="player-review-answer">
                            <strong>{question.text}</strong>
                            <small className={!answered && question.required && !readOnly ? "is-required" : ""}>{question.required ? "Obligatoria" : "Opcional"} · {answered ? "Respondida" : "Pendiente"}</small>
                            <span className={!answered ? "is-unanswered" : ""}>{display}</span>
                          </span>
                          <i className={`pi ${readOnly ? "pi-arrow-right" : "pi-pencil"}`} aria-hidden="true" />
                        </button>
                      </li>;
                    })}
                  </ol>
                </>
              ) : (
                <>
                  <div className="player-question-meta">
                    <span>Pregunta {idx + 1} de {total}</span>
                    <span>{answerTypeLabels[current.type]}</span>
                    <span className={current.required ? "player-required" : ""}>{current.required ? "Obligatoria" : "Opcional"}</span>
                  </div>
                  <h2 id="player-question-title" ref={questionHeading} tabIndex={-1} className="player-question-title">{current.text}</h2>
                  <fieldset key={current.id} ref={questionRoot} disabled={locked || deadline.closed} className="assignment-answer"
                    aria-labelledby="player-question-title" aria-describedby={validationError ? "assignment-validation" : undefined}>
                    {current.type === "text" && <InputTextarea rows={7}
                      aria-labelledby="player-question-title" aria-required={!!current.required} aria-invalid={!!validationError}
                      aria-describedby={validationError ? "assignment-validation" : undefined}
                      value={answers[current.id] ?? ""} onChange={event => !readOnly && setAnswer(current.id, event.target.value)}
                      placeholder="Escribe tu respuesta..." readOnly={readOnly || deadline.closed} />}
                    {current.type === "number" && <InputNumber
                      inputId={`answer-${current.id}`} aria-labelledby="player-question-title"
                      aria-required={!!current.required} aria-invalid={!!validationError} aria-describedby={validationError ? "assignment-validation" : undefined}
                      value={answers[current.id] ?? null} onValueChange={event => !readOnly && setAnswer(current.id, event.value)}
                      onChange={event => !readOnly && setAnswer(current.id, event.value)}
                      placeholder="Ingresa un número" useGrouping={false} disabled={readOnly || deadline.closed} />}
                    {(current.type === "single" || current.type === "multi") && <div className="player-options">
                      {(current.options || []).map((option, position) => {
                        const values = Array.isArray(answers[current.id]) ? answers[current.id] : [];
                        const checked = current.type === "single" ? answers[current.id] === option : values.includes(option);
                        return <label key={option} htmlFor={`answer-${current.id}-${position}`}
                          className={`player-option${checked ? " is-selected" : ""}${readOnly || locked ? " is-locked" : ""}`}>
                          {current.type === "single" ? <RadioButton
                            inputId={`answer-${current.id}-${position}`} name={current.id} value={option}
                            onChange={event => !readOnly && setAnswer(current.id, event.value)} checked={checked} disabled={readOnly}
                          /> : <Checkbox inputId={`answer-${current.id}-${position}`}
                            onChange={event => {
                              if (!readOnly) setAnswer(current.id, event.checked ? [...values, option] : values.filter(value => value !== option));
                            }} checked={checked} disabled={readOnly} />}
                          <span>{option}</span>
                        </label>;
                      })}
                    </div>}
                  </fieldset>
                  {validationError && <p id="assignment-validation" role="alert" className="ta-field-error">{validationError}</p>}
                </>
              )}
            </div>
          </div>
          {total > 0 && <footer className="player-footer">
            <Button icon="pi pi-arrow-left" label={review ? "Volver a la pregunta" : "Anterior"} text
              disabled={locked || (!review && !canPrev)} onClick={review ? () => goToQuestion(idx) : onPrev} />
            <div className="player-footer-forward">
              {!review && <span className="player-position">{idx + 1} / {total}</span>}
              {review ? readOnly ? <Button label="Volver a asignaciones" icon="pi pi-arrow-left" outlined onClick={() => requestLeave("/assignments")} />
                : <Button label="Enviar" icon="pi pi-send" loading={submitting} disabled={locked || deadline.closed} onClick={() => onSubmit()} />
                : canNext ? <Button label="Siguiente" icon="pi pi-arrow-right" iconPos="right" disabled={locked} onClick={onNext} />
                  : <Button label={readOnly ? "Ver resumen" : "Revisar y enviar"} icon="pi pi-list-check" disabled={locked} onClick={showReview} />}
            </div>
          </footer>}
        </section>
      </div>
      {deadline.closed && <AssignmentDeadlineDialog status={deadline.status} onReturn={() => navigate("/assignments")} onDownload={downloadDeadlineBackup} />}
      <ModalShell visible={confirmIncomplete && !deadline.closed} title="Enviar evaluación incompleta" width="32rem" onHide={() => setConfirmIncomplete(false)}
        okLabel="Enviar de todos modos" onConfirm={() => onSubmit(true)} okLoading={submitting}
        cancelLabel="Seguir respondiendo">
        <p>Quedan {total - answeredCount} preguntas sin responder{missingRequired ? `, incluidas ${missingRequired} obligatorias` : ""}. Se enviará lo contestado y ya no podrás modificar esta evaluación.</p>
      </ModalShell>
      <ModalShell visible={!!leaveTo && !deadline.closed} title="Hay respuestas sin guardar" width="32rem" onHide={() => setLeaveTo(null)}
        footer={<div className="assignment-dialog-actions"><Button type="button" label="Seguir respondiendo" text onClick={() => setLeaveTo(null)} />
          <Button type="button" icon="pi pi-save" label="Guardar y salir" loading={savingDraft} disabled={savingDraft}
            onClick={async () => { if (await draft.save()) navigate(leaveTo); }} /></div>}>
        <p>Guarda el progreso antes de salir de esta evaluación.</p>
      </ModalShell>
      <Dialog visible={!!draft.recovery && !deadline.closed} header="Recuperar respuestas" className="teamassess-dialog" style={{ width: "32rem" }}
        closable={false} closeOnEscape={false} draggable={false} onHide={() => {}}
        footer={<div className="assignment-dialog-actions"><Button type="button" label="Usar versión del servidor" text onClick={draft.discardRecovery} />
          <Button type="button" label="Recuperar borrador" icon="pi pi-history" onClick={draft.recover} /></div>}>
        <p>Esta pestaña tiene respuestas diferentes al último progreso guardado.</p>
      </Dialog>
    </div>
  );
}
